// configurator-quantify.js
// Quantify rebuild configurator (build order step 6, v1 test round).
// Posts the Section 2.2 payload to the whl-create-bundle Supabase edge function
// and consumes its response contract (bundleVariantId, variantId, total in cents,
// isDuplicate, bundleHandle). Quote cart integration is carried over from the
// legacy configurator unchanged: localStorage sms_quote_cart fallback, quote
// threshold, SMS_QuoteModal, QuoteSuccessDialog, quote-cart-updated event.
// Warranty markups come ONLY from the settings action (single source rule).
// Memory: one radio card per module x count pair; qty carried in data-qty and
// pricing handled by the generic group loop; the capacity dropdown filters cards
// by data-total-gb. The RAM Clock Speed filter has a Clear Selection link
// (.q-clear-speed-filter) relocated by JS into the speed button row itself; it
// deactivates the speed filter so all speeds render for comparison (2026-08-10;
// capacity filter and selection untouched).
// Card prices render absolute on the first line with the bracketed delta below
// (owner preference).
// Drives: Recommended group always visible; per-family inline expanders with
// Show/Hide label swap. Drive quantity IS the selection: steppers start at 0,
// qty > 0 auto-adds, back to 0 auto-removes, no Add button; cross-family bay
// clamp enforced globally (plus buttons freeze, typed values clamp).
// TDP (reworked 2026-08-12): computeTdpBreakdown() builds a per-section watts
// breakdown (chassis base plus steps 1-8; OS draws nothing and is omitted).
// Drive watts DEDUPE BY QSKU: twin cards (Recommended plus home group, the
// duplicate-not-remove ruling) share one qsku and must count ONCE; the old loop
// counted both DOM inputs and inflated TDP. A tooltip trigger injected next to
// .product-summary-watts shows every row INCLUDING 0W rows (owner ruling
// 2026-08-12: zeros stay visible so buyers never wonder whether a category was
// included, and data gaps surface as suspicious zeros).
// Instructions boxes (owner request 2026-08-12): configurator-info-box renders
// as a .q-info-expander details, collapsed by default, plus glyph flipping to
// minus when open; the section summary click handler excludes it (same pattern
// as .q-drive-expander) so it toggles independently. Its styles live in the
// shared injected style block below.
// Known v1 notes: Add To Cart fails on DRAFT test bundles by design (resolves at
// cutover when bundles publish); page prices may drift from engine prices until
// the whl-push cutover, the backend total is authoritative.

class ServerConfiguratorQuantify extends HTMLElement {
  constructor() {
    super();
    this.selectedDrives = new Map(); // qsku -> {qty, unitCents, ff, title}
    this.maxDriveSlots = 0;
    this.warrantyTiers = null; // [{label, markup_percent}]
    this.settingsError = null;

    window.addEventListener('pageshow', (event) => {
      if (event.persisted || (performance.getEntriesByType('navigation')[0]?.type === 'back_forward')) {
        window.location.reload();
      }
    });

    this.init();
  }

  init() {
    this.maxDriveSlots = parseInt(this.dataset.maxDrives) || 0;
    this.setupRelatedServerDropdown();
    this.setupDetailsAnimation();
    this.setupInfoModals();
    this.setupFilters();
    this.setupCardSelectors();
    this.setupMemory();
    this.setupDrives();
    this.setupOsDropdowns();
    this.setupWarranty();
    this.setupActions();
    this.setupTdpTooltip();
    this.loadSettings();
    this.recalc();
  }

  // ---------- settings (warranty markups, single source) ----------

  async loadSettings() {
    try {
      const resp = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.dataset.anonKey}`
        },
        body: JSON.stringify({ action: 'settings' })
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok || !Array.isArray(data.warranty_tiers)) {
        throw new Error(data.error || 'settings unavailable');
      }
      this.warrantyTiers = data.warranty_tiers;
      this.settingsError = null;
    } catch (err) {
      console.error('Quantify settings load failed:', err);
      this.settingsError = String(err.message || err);
      this.warrantyTiers = null;
    }
    this.recalc();
  }

  tierMarkup(label) {
    if (!this.warrantyTiers) return null;
    const t = this.warrantyTiers.find((x) => x.label === label);
    return t ? t.markup_percent : null;
  }

  // ---------- navigation / layout ----------

  setupRelatedServerDropdown() {
    this.querySelector('select[name="related-server-selection"]')?.addEventListener('change', function () {
      if (this.value) window.location.href = this.value;
    });
  }

  setupDetailsAnimation() {
    this.querySelectorAll('.configurator__section').forEach((section) => {
      const content = section.querySelector('.configurator__content-wrapper');
      let isAnimating = false;
      section.addEventListener('click', (event) => {
        if (!event.target.closest('summary')) return;
        if (event.target.closest('.q-drive-expander')) return;
        if (event.target.closest('.q-info-expander')) return;
        event.preventDefault();
        if (isAnimating) return;
        isAnimating = true;
        if (!section.open) {
          section.open = true;
          content.style.height = '0px';
          requestAnimationFrame(() => {
            content.style.height = content.scrollHeight + 'px';
            content.style.opacity = '1';
          });
        } else {
          content.style.height = content.scrollHeight + 'px';
          content.offsetHeight;
          requestAnimationFrame(() => {
            content.style.height = '0px';
            content.style.opacity = '0';
          });
        }
      });
      content.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'height') {
          isAnimating = false;
          if (content.style.height === '0px') section.open = false;
          else content.style.height = 'auto';
        }
      });
    });
  }

  setupInfoModals() {
    this.querySelectorAll('.configurator__info-modal-button').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = document.querySelector(`.configurator__info-modal[data-modal-type="${button.dataset.modalType}"]`);
        if (modal) this.openModal(modal);
      });
    });
    document.querySelectorAll('.configurator__modal-close').forEach((btn) => {
      btn.addEventListener('click', (e) => this.closeModal(e.target.closest('.configurator__modal')));
    });
    document.querySelectorAll('.configurator__modal').forEach((modal) => {
      modal.addEventListener('click', (e) => {
        if (e.target.classList.contains('configurator__modal')) this.closeModal(e.target);
      });
    });
  }

  openModal(modal) {
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => modal.classList.add('configurator__modal--visible'));
  }

  closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('configurator__modal--visible');
    modal.addEventListener('transitionend', function handler(e) {
      if (e.target === modal && e.propertyName === 'opacity') {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        modal.removeEventListener('transitionend', handler);
      }
    });
  }

  // ---------- filters ----------

  setupFilters() {
    this.querySelectorAll('.configurator__filter-btn').forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('.configurator__filter-group');
        group.querySelectorAll('.configurator__filter-btn').forEach((b) => b.classList.remove('configurator__filter-btn--active'));
        button.classList.add('configurator__filter-btn--active');
        const section = button.closest('.configurator__section');
        this.applySectionFilters(section);
        if (button.dataset.filterField === 'filter_operation_system') {
          this.updateOsSelectVisibility(button.dataset.filterValue);
        }
        if (section?.open) {
          const content = section.querySelector('.configurator__content-wrapper');
          if (content) content.style.height = 'auto';
        }
      });
    });
    // Clear Selection link for the RAM Clock Speed filter (2026-08-10): relocate
    // it into the speed button row so it sits inline with the buttons, then bind.
    // Clicking deactivates the speed filter so every speed's cards render side by
    // side for comparison.
    const clearLink = this.querySelector('.q-clear-speed-filter');
    const speedBtn = this.querySelector('.configurator__filter-btn[data-filter-field="filter_memory_speed"]');
    if (clearLink && speedBtn && speedBtn.parentElement) {
      speedBtn.parentElement.appendChild(clearLink);
      clearLink.style.alignSelf = 'center';
      clearLink.style.justifySelf = 'start';
      clearLink.style.whiteSpace = 'nowrap';
      clearLink.style.margin = '0';
      this.querySelector('.q-clear-speed-filter-wrap')?.remove();
    }
    this.querySelectorAll('.q-clear-speed-filter').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const section = link.closest('.configurator__section');
        if (!section) return;
        section.querySelectorAll('.configurator__filter-btn--active').forEach((b) => {
          if (b.dataset.filterField === 'filter_memory_speed') {
            b.classList.remove('configurator__filter-btn--active');
          }
        });
        this.applySectionFilters(section);
        if (section.open) {
          const content = section.querySelector('.configurator__content-wrapper');
          if (content) content.style.height = 'auto';
        }
      });
    });
    this.querySelectorAll('.configurator__section').forEach((s) => this.applySectionFilters(s));
  }

  activeFilters(section) {
    const filters = {};
    section.querySelectorAll('.configurator__filter-btn--active').forEach((b) => {
      if (b.dataset.filterField && b.dataset.filterValue) filters[b.dataset.filterField] = b.dataset.filterValue;
    });
    return filters;
  }

  applySectionFilters(section) {
    const filters = this.activeFilters(section);
    const fields = Object.keys(filters);
    section.querySelectorAll('.configurator__card').forEach((card) => {
      const relevant = fields.filter((f) => card.dataset[f] !== undefined);
      if (!relevant.length && !card.classList.contains('q-memory-card')) return;
      const matches = relevant.every((f) => card.dataset[f] === filters[f]);
      const extra = card.classList.contains('q-memory-card') ? this.memoryCardMatchesCapacity(card) : true;
      card.style.display = matches && extra ? '' : 'none';
    });
  }

  // ---------- generic card selection ----------

  setupCardSelectors() {
    this.querySelectorAll('.configurator__selector').forEach((selector) => {
      selector.addEventListener('change', (event) => {
        if (!event.target.checked) return;
        const card = event.target.closest('.configurator__card');
        const section = event.target.closest('.configurator__section') || event.target.closest('.configurator__add-ons');
        const scope = section || this;
        scope.querySelectorAll(`input[name="${event.target.name}"]`).forEach((input) => {
          input.closest('.configurator__card')?.classList.remove('configurator__card--selected');
        });
        card?.classList.add('configurator__card--selected');
        this.recalc();
      });
    });
  }

  // ---------- memory (one radio card per module x count) ----------

  setupMemory() {
    const capFilter = this.querySelector('#q-memory-capacity-filter');
    if (!capFilter) return;
    const totals = new Set();
    this.querySelectorAll('#configurator-memory .q-memory-card').forEach((card) => {
      const gb = parseInt(card.dataset.totalGb) || 0;
      if (gb > 0) totals.add(gb);
    });
    Array.from(totals).sort((a, b) => a - b).forEach((gbTotal) => {
      const opt = document.createElement('option');
      opt.value = String(gbTotal);
      opt.textContent = this.formatGB(gbTotal);
      capFilter.appendChild(opt);
    });
    capFilter.addEventListener('change', () => {
      const section = capFilter.closest('.configurator__section');
      this.applySectionFilters(section);
      this.recalc();
      if (section?.open) {
        const content = section.querySelector('.configurator__content-wrapper');
        if (content) content.style.height = 'auto';
      }
    });
  }

  memoryCardMatchesCapacity(card) {
    const capFilter = this.querySelector('#q-memory-capacity-filter');
    if (!capFilter || capFilter.value === 'all') return true;
    return card.dataset.totalGb === capFilter.value;
  }

  selectedMemory() {
    return this.readRadio('memory-selection');
  }

  // ---------- drives ----------

  setupDrives() {
    this.querySelectorAll('.q-drive-expander').forEach((expander) => {
      const summary = expander.querySelector('summary');
      const label = expander.dataset.groupLabel;
      if (!summary || !label) return;
      expander.addEventListener('toggle', () => {
        summary.textContent = `${expander.open ? 'Hide' : 'Show'} ${label}`;
      });
    });
    this.querySelectorAll('.q-drive-group .quantity__button').forEach((button) => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity__input');
        const cur = parseInt(input.value) || 0;
        input.value = button.name === 'plus' ? cur + 1 : cur - 1;
        this.applyDriveQty(input);
      });
    });
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      input.addEventListener('change', () => this.applyDriveQty(input));
      input.addEventListener('wheel', (e) => e.preventDefault());
    });
  }

  usedDriveSlots() {
    let used = 0;
    this.selectedDrives.forEach((d) => { used += d.qty; });
    return used;
  }

  applyDriveQty(input) {
    const qsku = input.dataset.driveQsku;
    const current = this.selectedDrives.get(qsku)?.qty || 0;
    const remaining = this.maxDriveSlots - this.usedDriveSlots() + current;
    let wanted = parseInt(input.value) || 0;
    wanted = Math.max(0, Math.min(wanted, remaining));
    input.value = wanted;
    const card = input.closest('.configurator__card');
    if (wanted === 0) {
      this.selectedDrives.delete(qsku);
      card?.classList.remove('configurator__card--selected');
    } else {
      this.selectedDrives.set(qsku, {
        qty: wanted,
        unitCents: parseInt(input.dataset.unitCents) || 0,
        ff: input.dataset.driveFf,
        title: input.dataset.title
      });
      card?.classList.add('configurator__card--selected');
    }
    this.refreshDriveUi();
    this.recalc();
  }

  refreshDriveUi() {
    const used = this.usedDriveSlots();
    const counter = this.querySelector('.configurator__slots-used');
    if (counter) counter.textContent = used;
    const remaining = this.maxDriveSlots - used;
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      const qty = this.selectedDrives.get(input.dataset.driveQsku)?.qty || 0;
      input.max = qty + remaining;
      const plusBtn = input.parentElement.querySelector('button[name="plus"]');
      if (plusBtn) plusBtn.disabled = remaining <= 0;
    });
  }

  // ---------- OS ----------

  setupOsDropdowns() {
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      select.addEventListener('change', () => {
        this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((other) => {
          if (other !== select) other.selectedIndex = 0;
        });
        const errorMessage = select.closest('.configurator__section')?.querySelector('.configurator__error-message');
        if (errorMessage) errorMessage.style.display = select.value ? 'none' : 'block';
        this.recalc();
      });
    });
  }

  updateOsSelectVisibility(selectedType) {
    const handleized = selectedType.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    this.querySelectorAll('.configurator__dropdown-wrapper[data-os-type]').forEach((wrapper) => {
      wrapper.style.display = wrapper.dataset.osType === handleized ? '' : 'none';
    });
  }

  selectedOs() {
    for (const select of this.querySelectorAll('.configurator__dropdown[name="os-selection"]')) {
      const opt = select.options[select.selectedIndex];
      if (opt && opt.value !== '') {
        return {
          qsku: opt.dataset.qsku,
          unitCents: parseInt(opt.dataset.unitCents) || 0,
          title: opt.textContent.trim()
        };
      }
    }
    return null;
  }

  // ---------- warranty ----------

  setupWarranty() {
    const yearsSelect = this.querySelector('#q-warranty-years');
    if (yearsSelect) {
      yearsSelect.addEventListener('click', (e) => e.stopPropagation());
      yearsSelect.addEventListener('change', () => {
        const smsRadio = this.querySelector('input[name="warranty-selection"][value="sms-warranty"]');
        if (smsRadio && !smsRadio.checked) {
          smsRadio.checked = true;
          smsRadio.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.recalc();
        }
      });
    }
  }

  selectedWarrantyLabel() {
    const checked = this.querySelector('input[name="warranty-selection"]:checked');
    if (!checked || checked.value !== 'sms-warranty') return '180 days Warranty';
    const yearsSelect = this.querySelector('#q-warranty-years');
    return yearsSelect?.value || '180 days Warranty';
  }

  // ---------- selection readers ----------

  readRadio(name) {
    const input = this.querySelector(`input[name="${name}"]:checked`);
    if (!input || !input.dataset.qsku) return null;
    const qty = parseInt(input.dataset.qty) || 0;
    if (qty < 1) return null;
    return {
      qsku: input.dataset.qsku,
      qty,
      unitCents: parseInt(input.dataset.unitCents) || 0,
      title: input.closest('.configurator__card')?.querySelector('.configurator__card-title')?.textContent?.trim() || input.dataset.qsku
    };
  }

  buildLines() {
    const lines = [];
    lines.push({ sku: this.dataset.chassisQsku, qty: 1, role: 'chassis' });
    const proc = this.readRadio('processor-selection');
    if (proc) lines.push({ sku: proc.qsku, qty: proc.qty, role: 'processor' });
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) lines.push({ sku: heatsink.qsku, qty: heatsink.qty, role: 'heatsink' });
    const memory = this.selectedMemory();
    if (memory) lines.push({ sku: memory.qsku, qty: memory.qty, role: 'memory' });
    const raid = this.readRadio('raid-selection');
    if (raid) lines.push({ sku: raid.qsku, qty: raid.qty, role: 'raid' });
    this.selectedDrives.forEach((d, qsku) => {
      lines.push({ sku: qsku, qty: d.qty, role: 'drive', drive_ff: d.ff });
    });
    const remote = this.readRadio('remote-selection');
    if (remote) lines.push({ sku: remote.qsku, qty: remote.qty, role: 'remote' });
    const psu = this.readRadio('power-supply-selection');
    if (psu) lines.push({ sku: psu.qsku, qty: psu.qty, role: 'psu' });
    const nic = this.readRadio('network-card-selection');
    if (nic) lines.push({ sku: nic.qsku, qty: nic.qty, role: 'nic' });
    const os = this.selectedOs();
    if (os) lines.push({ sku: os.qsku, qty: 1, role: 'os' });
    return lines;
  }

  // ---------- pricing (page prices; backend total is authoritative) ----------

  baseTotalCents() {
    let total = parseInt(this.dataset.chassisCents) || 0;
    const proc = this.readRadio('processor-selection');
    if (proc) total += proc.unitCents * proc.qty;
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) total += heatsink.unitCents * heatsink.qty;
    const memory = this.selectedMemory();
    if (memory) total += memory.unitCents * memory.qty;
    const raid = this.readRadio('raid-selection');
    if (raid) total += raid.unitCents * raid.qty;
    this.selectedDrives.forEach((d) => { total += d.unitCents * d.qty; });
    const remote = this.readRadio('remote-selection');
    if (remote) total += remote.unitCents * remote.qty;
    const psu = this.readRadio('power-supply-selection');
    if (psu) total += psu.unitCents * psu.qty;
    const nic = this.readRadio('network-card-selection');
    if (nic) total += nic.unitCents * nic.qty;
    const os = this.selectedOs();
    if (os) total += os.unitCents;
    return total;
  }

  // ---------- TDP breakdown (reworked 2026-08-12) ----------

  computeTdpBreakdown() {
    // Radio-driven sections: watts = data-consumption x data-qty on the checked
    // input. Sections whose Liquid renders no data-consumption read 0 here;
    // 0W rows stay visible in the tooltip by design.
    const radioWatts = (name) => {
      const input = this.querySelector(`input[name="${name}"]:checked`);
      if (!input) return { watts: 0, detail: '' };
      const c = parseFloat(input.dataset.consumption) || 0;
      if (c === 0) return { watts: 0, detail: '' };
      const qty = Math.max(1, parseInt(input.dataset.qty) || 1);
      return { watts: c * qty, detail: qty > 1 ? `${qty}x ${c}W` : '' };
    };

    // Drives: DEDUPE BY QSKU. A recommended drive renders twice (Recommended
    // section plus its home group, duplicate-not-remove ruling); both DOM
    // inputs share one qsku and one selectedDrives entry, so counting each
    // input double-counted the watts. Count each qsku once.
    let driveWatts = 0;
    const seenQskus = new Set();
    this.querySelectorAll('.q-drive-group .quantity__input').forEach((input) => {
      const qsku = input.dataset.driveQsku;
      if (!qsku || seenQskus.has(qsku)) return;
      seenQskus.add(qsku);
      const qty = this.selectedDrives.get(qsku)?.qty || 0;
      if (qty > 0) {
        const priceEl = input.closest('.configurator__card')?.querySelector('.q-drive-price');
        driveWatts += (parseFloat(priceEl?.dataset.consumption) || 0) * qty;
      }
    });

    const rows = [
      Object.assign({ label: 'Chassis Base' }, { watts: parseFloat(this.dataset.baseConsumption) || 0, detail: '' }),
      Object.assign({ label: 'Step 1: Processor' }, radioWatts('processor-selection')),
      Object.assign({ label: 'Step 2: Heat Sink' }, radioWatts('heatsink-selection')),
      Object.assign({ label: 'Step 3: Memory (RAM)' }, radioWatts('memory-selection')),
      Object.assign({ label: 'Step 4: RAID Controllers' }, radioWatts('raid-selection')),
      Object.assign({ label: 'Step 5: Storage Drives' }, { watts: driveWatts, detail: '' }),
      Object.assign({ label: 'Step 6: Remote Access' }, radioWatts('remote-selection')),
      Object.assign({ label: 'Step 7: Power Supply' }, radioWatts('power-supply-selection')),
      Object.assign({ label: 'Step 8: Network Cards' }, radioWatts('network-card-selection'))
    ];
    const total = rows.reduce((sum, r) => sum + r.watts, 0);
    return { rows, total };
  }

  setupTdpTooltip() {
    if (document.querySelector('.q-tdp-wrap')) return;
    const style = document.createElement('style');
    style.textContent = `
      .q-tdp-wrap { position: relative; display: inline-flex; align-items: center; gap: 0.35rem; }
      .q-tdp-trigger { width: 1.1rem; height: 1.1rem; border-radius: 50%; border: 1px solid currentColor; background: none; color: inherit; font-size: 0.7rem; font-weight: 600; line-height: 1; cursor: pointer; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
      .q-tdp-tip { display: none; position: absolute; bottom: calc(100% + 0.5rem); right: 0; background: #fff; color: #222; border: 1px solid #ddd; border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); padding: 0.75rem 1rem; min-width: 260px; z-index: 100; font-size: 0.85rem; text-align: left; }
      .q-tdp-wrap.q-tdp-open .q-tdp-tip { display: block; }
      .q-tdp-row { display: flex; justify-content: space-between; gap: 1.5rem; padding: 0.15rem 0; white-space: nowrap; }
      .q-tdp-row--total { border-top: 1px solid #ddd; margin-top: 0.35rem; padding-top: 0.4rem; font-weight: 600; }
      .q-tdp-detail { opacity: 0.65; font-size: 0.9em; margin-left: 0.35rem; }
      .q-info-expander__summary { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; cursor: pointer; list-style: none; font-weight: 600; }
      .q-info-expander__summary::-webkit-details-marker { display: none; }
      .q-info-expander__summary::marker { content: ''; }
      .q-info-expander__icon { flex: none; width: 1.3rem; height: 1.3rem; border-radius: 50%; border: 1px solid currentColor; display: inline-flex; align-items: center; justify-content: center; font-size: 1rem; line-height: 1; }
      .q-info-expander__icon::before { content: '+'; }
      .q-info-expander[open] .q-info-expander__icon::before { content: '\\2212'; }
      .q-info-expander__body { margin-top: 0.75rem; }
    `;
    document.head.appendChild(style);
    document.querySelectorAll('.product-summary-watts').forEach((el) => {
      const wrap = document.createElement('span');
      wrap.className = 'q-tdp-wrap';
      el.parentNode.insertBefore(wrap, el);
      wrap.appendChild(el);
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'q-tdp-trigger';
      trigger.setAttribute('aria-label', 'Show estimated power draw breakdown');
      trigger.textContent = 'i';
      const tip = document.createElement('div');
      tip.className = 'q-tdp-tip';
      wrap.appendChild(trigger);
      wrap.appendChild(tip);
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        wrap.classList.toggle('q-tdp-open');
      });
      wrap.addEventListener('mouseenter', () => wrap.classList.add('q-tdp-open'));
      wrap.addEventListener('mouseleave', () => wrap.classList.remove('q-tdp-open'));
    });
    document.addEventListener('click', () => {
      document.querySelectorAll('.q-tdp-wrap.q-tdp-open').forEach((w) => w.classList.remove('q-tdp-open'));
    });
  }

  updateTdpTooltip(breakdown) {
    const rowsHtml = breakdown.rows.map((r) => {
      const detail = r.detail ? `<span class="q-tdp-detail">(${r.detail})</span>` : '';
      return `<div class="q-tdp-row"><span>${r.label}${detail}</span><span>${Math.round(r.watts)}W</span></div>`;
    }).join('');
    const totalHtml = `<div class="q-tdp-row q-tdp-row--total"><span>Estimated TDP</span><span>${Math.round(breakdown.total)}W</span></div>`;
    document.querySelectorAll('.q-tdp-tip').forEach((tip) => {
      tip.innerHTML = rowsHtml + totalHtml;
    });
  }

  recalc() {
    const base = this.baseTotalCents();

    // Hybrid absolute + delta on every radio card group (ratified 4.2)
    const groups = ['processor-selection', 'heatsink-selection', 'memory-selection', 'raid-selection', 'remote-selection', 'power-supply-selection', 'network-card-selection'];
    groups.forEach((name) => {
      const checked = this.querySelector(`input[name="${name}"]:checked`);
      const selectedLine = checked ? (parseInt(checked.dataset.unitCents) || 0) * (parseInt(checked.dataset.qty) || 0) : 0;
      this.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
        const priceEl = input.closest('.configurator__card')?.querySelector('.q-price');
        if (!priceEl || priceEl.dataset.included) return;
        const line = (parseInt(input.dataset.unitCents) || 0) * (parseInt(input.dataset.qty) || 0);
        if (input === checked) {
          priceEl.textContent = this.formatMoney(line);
        } else {
          const diff = line - selectedLine;
          priceEl.innerHTML = `${this.formatMoney(line)}<span class="q-price-delta" style="display:block;font-size:0.85em;opacity:0.75;">[${diff >= 0 ? '+' : ''}${this.formatMoney(diff)}]</span>`;
        }
      });
    });

    // Drive prices: unit price at qty 0, absolute line total once added
    this.querySelectorAll('.q-drive-price').forEach((priceEl) => {
      const card = priceEl.closest('.configurator__card');
      const input = card.querySelector('.quantity__input');
      const qty = parseInt(input.value) || 0;
      const unit = parseInt(priceEl.dataset.unitCents) || 0;
      priceEl.textContent = `+${this.formatMoney(qty > 0 ? unit * qty : unit)}`;
    });

    // OS option labels: absolute + delta
    const osSel = this.selectedOs();
    const osSelCents = osSel ? osSel.unitCents : 0;
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach((select) => {
      Array.from(select.options).forEach((opt) => {
        if (opt.value === '') return;
        const cents = parseInt(opt.dataset.unitCents) || 0;
        const baseText = opt.textContent.split(' [')[0].split(' (')[0].trim();
        const diff = cents - osSelCents;
        if (osSel && opt.dataset.qsku === osSel.qsku && select.value === opt.value) {
          opt.textContent = cents > 0 ? `${baseText} (${this.formatMoney(cents)})` : baseText;
        } else if (cents === 0 && diff === 0) {
          opt.textContent = baseText;
        } else {
          opt.textContent = `${baseText} (${this.formatMoney(cents)}, ${diff >= 0 ? '+' : ''}${this.formatMoney(diff)})`;
        }
      });
    });

    // Warranty price from settings markups
    const warrantyPriceEl = this.querySelector('.q-warranty-price');
    const yearsSelect = this.querySelector('#q-warranty-years');
    if (warrantyPriceEl && yearsSelect) {
      const markup = this.tierMarkup(yearsSelect.value);
      if (markup === null) {
        warrantyPriceEl.textContent = this.settingsError ? 'unavailable' : '...';
      } else {
        warrantyPriceEl.textContent = `+${this.formatMoney(Math.round(base * markup / 100))}`;
      }
    }

    // Total with warranty
    const label = this.selectedWarrantyLabel();
    const markup = this.tierMarkup(label);
    const total = markup === null ? base : Math.round(base * (1 + markup / 100));
    document.querySelectorAll('.product-summary-price').forEach((el) => {
      el.textContent = this.formatMoney(total);
    });

    // TDP: per-section breakdown, drive twin dedupe, tooltip rows incl. 0W
    const breakdown = this.computeTdpBreakdown();
    document.querySelectorAll('.product-summary-watts').forEach((el) => {
      el.textContent = `${Math.round(breakdown.total)}W`;
    });
    this.updateTdpTooltip(breakdown);
  }

  // ---------- validation ----------

  validateSelections() {
    let valid = true;
    const requirements = [
      { grid: '#configurator-processors', ok: !!this.readRadio('processor-selection') },
      { grid: '#configurator-memory', ok: !!this.selectedMemory() },
      { grid: '#configurator-remotes', ok: !!this.readRadio('remote-selection') },
      { grid: '#configurator-power-supplies', ok: !!this.readRadio('power-supply-selection') },
      { grid: '#configurator-network-cards', ok: !!this.querySelector('input[name="network-card-selection"]:checked') }
    ];
    requirements.forEach((r) => {
      const grid = this.querySelector(r.grid);
      if (!grid) return;
      const details = grid.closest('details');
      const err = details?.querySelector('.configurator__error-message');
      if (!r.ok) {
        valid = false;
        if (details) details.style.border = '2px solid red';
        if (err) err.style.display = 'block';
      } else {
        if (details) details.style.border = '';
        if (err) err.style.display = 'none';
      }
    });
    const osOk = !!this.selectedOs();
    const osSection = this.querySelector('.configurator__dropdown[name="os-selection"]')?.closest('details');
    const osErr = osSection?.querySelector('.configurator__error-message');
    if (!osOk) {
      valid = false;
      if (osSection) osSection.style.border = '2px solid red';
      if (osErr) osErr.style.display = 'block';
    } else {
      if (osSection) osSection.style.border = '';
      if (osErr) osErr.style.display = 'none';
    }
    return valid;
  }

  // ---------- backend ----------

  async sendConfiguration() {
    const payload = {
      action: 'create',
      title: this.dataset.templateName,
      template_id: this.dataset.templateId,
      warranty_tier: this.selectedWarrantyLabel(),
      lines: this.buildLines()
    };
    const response = await fetch(this.dataset.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.dataset.anonKey}`
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) {
      const detail = Array.isArray(data.errors) && data.errors.length ? data.errors.join('; ') : (data.error || `HTTP ${response.status}`);
      throw new Error(detail);
    }
    return data;
  }

  getConfigurationProperties() {
    const properties = {};
    const names = [];
    const proc = this.readRadio('processor-selection');
    if (proc) names.push(`Processor: ${proc.title}`);
    const heatsink = this.readRadio('heatsink-selection');
    if (heatsink) names.push(`Heatsink: ${heatsink.title}`);
    const memory = this.selectedMemory();
    if (memory) names.push(`Memory: ${memory.title}`);
    const raid = this.readRadio('raid-selection');
    if (raid) names.push(`RAID Controller: ${raid.title}`);
    this.selectedDrives.forEach((d) => {
      names.push(d.qty > 1 ? `Storage Drive: ${d.qty}x ${d.title}` : `Storage Drive: ${d.title}`);
    });
    const remote = this.readRadio('remote-selection');
    if (remote) names.push(`Remote Access: ${remote.title}`);
    const psu = this.readRadio('power-supply-selection');
    if (psu) names.push(`Power Supply: ${psu.title}`);
    const nic = this.readRadio('network-card-selection');
    if (nic) names.push(`Network Card: ${nic.title}`);
    const os = this.selectedOs();
    if (os) names.push(`Operating System: ${os.title}`);
    names.push(`Warranty: ${this.selectedWarrantyLabel()}`);
    if (names.length) properties['_bundle_components'] = names.join('||');
    return properties;
  }

  createLoadingButton(button, alternateColor = false) {
    const existingText = button.textContent.trim();
    button.textContent = '';
    const buttonText = document.createElement('span');
    buttonText.className = 'button-text';
    buttonText.textContent = existingText;
    const spinner = document.createElement('span');
    spinner.className = `loading-spinner hidden ${alternateColor ? 'loading-spinner--alternate' : ''}`;
    spinner.innerHTML = `
      <svg aria-hidden="true" class="icon icon-spinner" viewBox="0 0 20 20">
        <path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z"/>
      </svg>`;
    button.appendChild(buttonText);
    button.appendChild(spinner);
    return {
      startLoading: () => {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('button--loading');
        button.disabled = true;
        spinner.classList.remove('hidden');
      },
      stopLoading: () => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.classList.remove('button--loading');
        spinner.classList.add('hidden');
      }
    };
  }

  setupActions() {
    document.querySelector('[data-add-to-cart-configurator]')?.addEventListener('click', () => this.addToCart());
    document.querySelector('[data-quote-configurator]')?.addEventListener('click', () => this.requestQuote());
  }

  async addToCart() {
    const button = document.querySelector('[data-add-to-cart-configurator]');
    if (!button) return;
    if (!this.validateSelections()) {
      alert('Please complete the required selections before adding to cart.');
      return;
    }
    const loadingButton = this.createLoadingButton(button);
    try {
      loadingButton.startLoading();
      const configResponse = await this.sendConfiguration();
      const variantId = configResponse.bundleVariantId || configResponse.variantId;
      if (!variantId) throw new Error('Invalid configuration response');
      if (configResponse.isDuplicate) console.log('Using existing bundle:', configResponse.bundleCode);
      const matches = String(variantId).match(/ProductVariant\/(\d+)/);
      const variantNumericId = matches ? matches[1] : null;
      if (!variantNumericId) throw new Error('Invalid variant ID format');
      const formData = {
        items: [{
          id: variantNumericId,
          quantity: 1,
          properties: {
            _total_price: configResponse.total,
            ...this.getConfigurationProperties()
          }
        }]
      };
      const maxRetries = 5;
      const retryDelay = 5000;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const response = await fetch(`${window.location.origin}/cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(formData)
        });
        const responseData = await response.json();
        if (response.ok) {
          window.location.href = window.location.origin + '/cart';
          return;
        }
        if (response.status === 422 && attempt < maxRetries) {
          console.warn(`Cart add attempt ${attempt} failed (422), retrying in ${retryDelay / 1000}s...`, responseData);
          await new Promise((r) => setTimeout(r, retryDelay));
          continue;
        }
        throw new Error(responseData.description || 'Error adding to cart (note: test-phase bundles are DRAFT and cannot be carted until cutover)');
      }
    } catch (error) {
      console.error('Detailed Error:', error);
      alert(`Failed to add item to cart: ${error.message}`);
    } finally {
      loadingButton.stopLoading();
    }
  }

  async requestQuote() {
    const button = document.querySelector('[data-quote-configurator]');
    if (!button) return;
    if (!this.validateSelections()) {
      alert('Please complete the required selections before requesting a quote.');
      return;
    }
    let loadingButton = null;
    try {
      loadingButton = this.createLoadingButton(button, true);
      loadingButton.startLoading();

      const configResponse = await this.sendConfiguration();
      const variantId = configResponse.bundleVariantId || configResponse.variantId;
      if (!variantId) throw new Error('Unable to create configuration. Please try again or contact support.');
      if (configResponse.isDuplicate) console.log('Using existing bundle:', configResponse.bundleCode);

      const { total } = configResponse;
      const properties = this.getConfigurationProperties();
      const productTitle = `${this.dataset.productTitle}-configured` || 'Server Configuration';
      let productImage = this.dataset.productImageUrl || '';
      if (productImage && productImage.startsWith('files/')) {
        productImage = productImage.replace('files/', 'https://savemyserver.com/cdn/shop/files/');
      }

      const cartItem = {
        variantId: variantId,
        quantity: 1,
        title: productTitle,
        price: total,
        image: productImage,
        properties: properties,
        configuration: {
          title: this.dataset.templateName,
          bundleCode: configResponse.bundleCode,
          lines: this.buildLines(),
          warranty_tier: this.selectedWarrantyLabel()
        }
      };

      if (loadingButton) {
        loadingButton.stopLoading();
        loadingButton = null;
      }

      const thresholdDollars = parseFloat(this.dataset.quoteThreshold) || 500;
      const thresholdCents = thresholdDollars * 100;

      if (total < thresholdCents) {
        this.saveToDraftCartFallback(cartItem);
        document.dispatchEvent(new CustomEvent('quote-cart-updated', {
          detail: { action: 'add', item: cartItem }
        }));
        if (window.QuoteSuccessDialog && window.SMS_QUOTE_SETTINGS) {
          window.QuoteSuccessDialog.show({
            title: window.SMS_QUOTE_SETTINGS.addedTitle,
            message: window.SMS_QUOTE_SETTINGS.addedMessage,
            buttonText: 'View Quote Cart',
            buttonUrl: '/pages/quote-cart'
          });
        }
      } else {
        if (window.SMS_QuoteModal && window.SMS_QuoteModal.showForSingleItem) {
          window.SMS_QuoteModal.showForSingleItem(cartItem);
        } else {
          throw new Error('Quote modal system not loaded. Please refresh the page and try again.');
        }
      }
    } catch (error) {
      console.error('Quote Error:', error);
      let userMessage = 'Failed to create quote: ';
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        userMessage += 'Network connection error. Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        userMessage += 'Request timed out. Please try again.';
      } else {
        userMessage += error.message;
      }
      alert(userMessage);
    } finally {
      if (loadingButton) loadingButton.stopLoading();
    }
  }

  saveToDraftCartFallback(item) {
    const storageKey = 'sms_quote_cart';
    let cart;
    try {
      const cartData = localStorage.getItem(storageKey);
      cart = cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error reading quote cart:', error);
      cart = { items: [], createdAt: new Date().toISOString() };
    }
    const existingItem = cart.items.find((cartItem) => {
      if (cartItem.variantId !== item.variantId) return false;
      const props1 = cartItem.properties || {};
      const props2 = item.properties || {};
      const keys1 = Object.keys(props1).sort();
      const keys2 = Object.keys(props2).sort();
      if (keys1.length !== keys2.length) return false;
      for (const key of keys1) {
        if (props1[key] !== props2[key]) return false;
      }
      return true;
    });
    if (existingItem) {
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
    } else {
      const itemId = `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      cart.items.push({ id: itemId, ...item, addedAt: new Date().toISOString() });
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving to quote cart:', error);
      throw new Error('Unable to save to quote cart');
    }
  }

  // ---------- formatting ----------

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return '$0.00';
    }
  }

  formatGB(gb) {
    if (gb >= 1024 && gb % 1024 === 0) return `${gb / 1024}TB`;
    return `${gb}GB`;
  }
}

customElements.define('server-configurator-quantify', ServerConfiguratorQuantify);
